import { APIDetails } from './apis-details';

export const smartCVApi: APIDetails = {
  id: 'cv-pai',
  title: 'Smart CV Analysis API',
  subtitle:
    'AI-powered, job-specific CV analysis for smarter hiring decisions.',
  description: [
    'Recruiters and HR teams are often overwhelmed by the sheer volume of applications for a single job opening. Manually reviewing hundreds or thousands of CVs is time-consuming, inconsistent, and can unintentionally introduce bias. Traditional screening methods focus on basic keywords, failing to capture a candidate’s true potential, real experience, or overall suitability for the role—resulting in missed talent and longer hiring cycles.',
    'The Smart CV Analysis API addresses this challenge by intelligently evaluating every CV in direct relation to the job requirements. Instead of surface-level matching, it provides a clear understanding of how well each candidate aligns with the role. Recruiters receive easy-to-read compatibility scores along with meaningful insights into a candidate’s strengths, skill gaps, and experience relevance.',
    'Built for modern recruitment workflows, the API enables faster screening, consistent evaluations, and confident, data-driven hiring decisions. It supports large-scale recruitment needs, keeps candidate data organized for future reference, and provides valuable analytics to improve hiring strategies over time. By transforming raw resumes into actionable hiring intelligence, the Smart CV Analysis API helps organizations hire better talent—faster, fairer, and more efficiently.',
  ],
  overview: [
    'The Smart CV Analysis API helps recruiters instantly understand how well a candidate fits a job role.Instead of manually reviewing CVs, it delivers clear compatibility scores and easy-to-understand insights that support confident hiring decisions.',
    'Designed for fast-moving recruitment teams, the API streamlines candidate screening, reduces time-to-hire, and improves overall hiring quality.',
    'It enables HR platforms and ATS systems to handle large volumes of CVs effortlessly while maintaining consistency and fairness in evaluation.',
    'With built-in support for scalable analysis, historical tracking, and performance insights, the Smart CV Analysis API empowers businesses to hire smarter, faster, and more efficiently—turning CV data into meaningful hiring intelligence.',
  ],
  features: [
    {
      title: 'Job-Specific CV Analysis:',
      description:
        'Analyze candidate CVs against specific job descriptions and required skill sets for precise matching.',
    },
    {
      title: 'AI Compatibility Scoring:',
      description:
        'Uses vector-based similarity matching combined with LLM reasoning to generate accurate job-fit scores.',
    },
    {
      title: 'Detailed Evaluation Reports:',
      description:
        'Provides in-depth analysis covering skills, experience, education, gaps, and strengths.',
    },
    {
      title: 'Batch Processing:',
      description:
        'Efficiently processes large volumes of CVs in the background without blocking client applications.',
    },
    {
      title: 'Archiving Support:',
      description:
        'Safely archive CV analyses to preserve data without permanent deletion.',
    },
    {
      title: 'Analytics & Statistics:',
      description:
        'Monitor CV analysis lifecycle, processing status, and system usage through aggregated metrics.',
    },
    {
      title: 'Secure & Scalable Architecture',
      description:
        'Built to support enterprise HR systems with high availability and scalability.',
    },
  ],
  workflow: [
    'Recruiter submits CV and job requirements',
    'AI evaluates compatibility using LLM',
    'System generates detailed analysis',
    'Results are stored and indexed',
    'Recruiters retrieve analysis, statistics, or archive/delete records',
  ],
  endpoints: [
    {
      category: 'CV Analysis',
      items: [
        {
          category: 'Analyze CV for Job',
          method: 'POST',
          path: '/api/v1/cv/',
          description:
            'Creates job-specific CV analysis using vectorization and LLM-based evaluation',
        },
        {
          category: 'Get CV Analysis',
          method: 'GET',
          path: '/api/v1/cv/{cv_id}',
          description:
            'Retrieves complete CV analysis with compatibility score and details',
        },
        {
          category: 'List CV Analyses',
          method: 'GET',
          path: '/api/v1/cv/',
          description:
            'Returns paginated CV analyses with filtering by status, limit, and offset',
        },
        {
          category: 'Delete CV Analysis',
          method: 'DELETE',
          path: '/api/v1/cv/{cv_id}',
          description: 'Permanently deletes CV analysis and associated data',
        },
        {
          category: 'Archive CV Analysis',
          method: 'PUT',
          path: '/api/v1/cv/{cv_id}/archive',
          description: 'Archives a CV analysis for future reference',
        },
        {
          category: 'Get Analysis Statistics',
          method: 'GET',
          path: '/api/v1/cv/stats/overview',
          description: 'Provides overview statistics by processing status',
        },
      ],
    },
  ],
  apiDocumentation: [
    {
      title: 'Analyze CV',
      method: 'POST',
      path: '/api/v1/cv/',
      requestPath: 'POST /api/v1/cv/',
      description:
        'Creates job-specific CV analysis using vectorization and LLM-based evaluation',
      requestExample: {
        job_description: 'We are looking for a skilled Python developer...',
        required_skills: 'Python, Django, PostgreSQL, AWS',
        cv_text: 'John Doe\nSoftware Engineer with 5 years experience...',
      },
      responseExample: {
        id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        job_description: 'We are looking for a skilled Python developer...',
        required_skills: 'Python, Django, PostgreSQL, AWS',
        cv_text: 'John Doe\nSoftware Engineer with 5 years experience...',
        processing_status: 'pending',
        created_at: '2025-12-24T11:49:50.301Z',
        updated_at: '2025-12-24T11:49:50.301Z',
      },
      statusCode: '200 OK',
    },
    {
      title: 'List CV Analyses',
      method: 'GET',
      path: '/api/v1/cv/',
      requestPath:
        'GET /api/v1/cv/?processing_status=pending&limit=10&offset=0',
      description:
        'Retrieves a list of CV analyses filtered by status, limit, and offset',
      requestExample: {
        processing_status: 'pending',
        limit: 10,
        offset: 0,
      },
      responseExample: [
        {
          id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
          job_description: 'We are looking for a skilled Python developer...',
          required_skills: 'Python, Django, PostgreSQL, AWS',
          cv_text: 'John Doe\nSoftware Engineer with 5 years experience...',
          processing_status: 'pending',
          created_at: '2025-12-24T11:54:08.746Z',
          updated_at: '2025-12-24T11:54:08.746Z',
        },
      ],
      statusCode: '200 OK',
    },
    {
      title: 'Get CV Analysis',
      method: 'GET',
      path: '/api/v1/cv/{cv_id}',
      requestPath: 'GET /api/v1/cv/3fa85f64-5717-4562-b3fc-2c963f66afa6',
      description:
        'Retrieves complete CV analysis with compatibility score and detailed analysis results',
      requestExample: {
        cv_id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      },
      responseExample: {
        id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        job_description: 'We are looking for a skilled Python developer...',
        required_skills: 'Python, Django, PostgreSQL, AWS',
        cv_text: 'John Doe\nSoftware Engineer with 5 years experience...',
        processing_status: 'completed',
        created_at: '2025-12-24T11:54:57.321Z',
        updated_at: '2025-12-24T11:54:57.321Z',
        analysis_results: {
          ats_score: 82,
          skill_match_percentage: 78,
          experience_years: 5,
          education_level: "Bachelor's Degree",
          strengths: [
            'Strong Python expertise',
            'Experience with Django REST Framework',
          ],
          weaknesses: ['Limited cloud architecture experience'],
          recommendations: [
            'Highlight AWS projects',
            'Add system design experience',
          ],
          hr_feedback:
            'Candidate is technically strong and suitable for mid-level roles.',
          general_feedback: 'Good match with minor skill gaps.',
          overall_fit_score: 80,
          experience_score: 85,
          education_score: 75,
          hiring_recommendation: 'Recommended',
          confidence_level: 'High',
          created_at: '2025-12-24T11:54:57.321Z',
          updated_at: '2025-12-24T11:54:57.321Z',
        },
      },
      statusCode: '200 OK',
    },
    {
      title: 'Delete CV Analysis',
      method: 'DELETE',
      path: '/api/v1/cv/{cv_id}',
      requestPath: 'DELETE /api/v1/cv/3fa85f64-5717-4562-b3fc-2c963f66afa6',
      description: 'Permanently deletes a CV analysis and associated data',
      requestExample: {
        cv_id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      },
      responseExample: {
        message: 'CV analysis deleted successfully',
      },
      statusCode: '200 OK',
    },
    {
      title: 'Archive CV Analysis',
      method: 'POST',
      path: '/api/v1/cv/{cv_id}/archive',
      requestPath:
        'PUT /api/v1/cv/3fa85f64-5717-4562-b3fc-2c963f66afa6/archive',
      description: 'Archives a CV analysis for future reference',
      requestExample: {
        cv_id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
      },
      responseExample: {
        id: '3fa85f64-5717-4562-b3fc-2c963f66afa6',
        processing_status: 'archived',
        created_at: '2025-12-24T11:56:04.775Z',
        updated_at: '2025-12-24T11:56:04.775Z',
      },
      statusCode: '200 OK',
    },
    {
      title: 'Get CV Analysis Statistics',
      method: 'GET',
      path: '/api/v1/cv/stats/overview',
      requestPath: 'GET /api/v1/cv/stats/overview',
      description: 'Provides overview statistics by processing status',
      responseExample: {
        total_analyses: 120,
        completed_analyses: 90,
        pending_analyses: 25,
        failed_analyses: 5,
      },
      statusCode: '200 OK',
    },
  ],
  useCases: [
    {
      title: 'High-Volume Hiring',
      description:
        'Instantly screen thousands of CVs for a single role and shortlist the best candidates without manual effort.',
    },
    {
      title: 'Faster Time-to-Hire',
      description:
        'Reduce screening time by automatically ranking candidates based on job fit and relevance.',
    },
    {
      title: 'Fair & Consistent Screening',
      description:
        'Ensure every candidate is evaluated using the same criteria, minimizing bias and inconsistency.',
    },
    {
      title: 'ATS & HR Platform Enhancement',
      description:
        'Power applicant tracking systems with intelligent CV insights and compatibility scoring.',
    },
    {
      title: 'Recruitment Agencies',
      description:
        'Manage multiple clients and roles efficiently while delivering higher-quality shortlists.',
    },
    {
      title: 'Campus & Graduate Hiring',
      description:
        'Quickly evaluate large pools of entry-level candidates beyond simple keyword matching.',
    },
    {
      title: 'Talent Pool Management',
      description:
        'Store and reuse CV insights for future roles, enabling smarter long-term talent sourcing.',
    },
    {
      title: 'Data-Driven Hiring Strategy',
      description:
        'Use hiring analytics to identify skill gaps, role demand trends, and recruitment performance.',
    },
  ],
  targetUsers: [
    'HR Tech & SaaS Companies',
    'Recruitment Platform Developers',
    'Enterprises with high-volume hiring',
    'AI-powered hiring startups',
    'Software teams building ATS solutions',
  ],
};
